import LoginIcon from "@mui/icons-material/Login";
import { createTheme, ThemeProvider } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ErrorList from "_shared/_errors/_views/error-list";
import { KeyboardKey } from "_shared/_utilities/keyboard-keys";
import LoadingButton from "_shared/_views/loading/loading-button";
import EmailAddress, { InvalidEmailAddressError } from "_shared/email-addresses/email-address";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSession } from "users/__session/session-context";
import Password, { InvalidPasswordError } from "users/auth/credentials/passwords/password";
import UserCredentials, { InvalidCredentialsError } from "users/auth/credentials/user-credentials";

type LoginFormProps = {
  onEmailValueChanged?: (email: string) => void;
};

export default function LoginForm(props: Readonly<LoginFormProps>) {
  const [emailValue, setEmailValue] = useState<string>("");
  const [passwordValue, setPasswordValue] = useState<string>("");
  const [validEmail, setValidEmail] = useState<EmailAddress | null>(null);
  const [validPassword, setValidPassword] = useState<Password | null>(null);
  const [rememberMeChecked, setRememberMeChecked] = useState<boolean>(false);
  const [loginFailed, setLoginFailed] = useState<boolean>(false);
  const [errors, setErrors] = useState<CredentialErrors>({
    email: "",
    password: [],
  });

  const [signingIn, setSigningIn] = useState<boolean>(false);

  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname ?? "/";
  const darkTheme = createTheme({ palette: { mode: "dark" } });

  useEffect(
    () => {
      if (!emailValue && !passwordValue) return;

      try {
        const email = new EmailAddress(emailValue);
        setValidEmail(email);
        setErrors((prevErrors) => ({ ...prevErrors, email: "" }));
      } catch (error) {
        setValidEmail(null);
        if (error instanceof InvalidEmailAddressError) {
          setErrors((prevErrors) => ({ ...prevErrors, email: error.userFriendlyMessage }));
        }
      }

      try {
        const password = new Password(passwordValue);
        setValidPassword(password);
        setErrors((prevErrors) => ({ ...prevErrors, password: [] }));
      } catch (error) {
        setValidPassword(null);
        if (error instanceof InvalidPasswordError) {
          setErrors((prevErrors) => ({ ...prevErrors, password: error.issues }));
        }
      }
    },
    [emailValue, passwordValue]
  );

  async function handleLoginSubmit() {
    if (!validEmail || !validPassword) {
      throw new Error("Cannot submit invalid form");
    }

    setErrors({ email: "", password: [] });
    setLoginFailed(false);
    setSigningIn(true);

    try {
      await session.login(
        new UserCredentials(
          validEmail,
          validPassword
        ),
        rememberMeChecked
      );
      if (session.openEntityAgreements.length > 0) {
        navigate(`/entity-agreements/${session.openEntityAgreements[0].id}`, { replace: true });
        return;
      }

      navigate(from, { replace: true });
    } catch (error) {
      if (error instanceof InvalidCredentialsError) {
        setErrors({ general: ["Invalid email or password."] });
      } else {
        console.error(error);
        setErrors({ general: ["Unknown error occurred. Please try again later."] });
      }
      setLoginFailed(true);
    } finally {
      setSigningIn(false);
    }
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <PageTitle variant="h4" align="center">
        Sign In
      </PageTitle>
      <CredentialInput
        id="login-email-input"
        label="Email Address"
        value={emailValue}
        required
        name="email"
        variant="filled"
        margin="normal"
        fullWidth
        onKeyDown={(event) => {
          if (event.key === KeyboardKey.Enter && validEmail && validPassword) {
            void handleLoginSubmit();
          }
        }}
        onChange={(event) => {
          setEmailValue(event.target.value);
          props.onEmailValueChanged?.(event.target.value);
        }}
        type="email"
        autoComplete="username"
        error={loginFailed || (errors.email !== undefined && errors.email.length > 0)}
        helperText={<ErrorItem key={errors.email}>{errors.email}</ErrorItem>}
      />
      <CredentialInput
        id="login-password-input"
        label="Password"
        type="password"
        value={passwordValue}
        required
        name="password"
        variant="filled"
        autoComplete="current-password"
        margin="normal"
        fullWidth
        onChange={(event) => setPasswordValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === KeyboardKey.Enter && validEmail && validPassword)
            void handleLoginSubmit();
        }}
        error={loginFailed || (errors.password && errors.password.length > 0)}
        helperText={errors.password?.map((err: string) => (
          <ErrorItem key={err}>{err}</ErrorItem>
        ))}
      />
      <ErrorList errors={errors.general} />
      <RememberMeToggle
        label="Remember me"
        control={
          <Checkbox
            value="remember"
            checked={rememberMeChecked}
            onChange={(event) => setRememberMeChecked(event.target.checked)}
          />
        }
      />
      <LoginButton
        id="login-submit-button"
        startIcon={<LoginIcon />}
        disabled={!validEmail || !validPassword || signingIn}
        loading={signingIn}
        variant="contained"
        color="primary"
        fullWidth
        onClick={handleLoginSubmit}
      >
        {signingIn ? "Signing In..." : "Sign In"}
      </LoginButton>
    </ThemeProvider>
  );
}

const PageTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.white,
}));
const CredentialInput = styled(TextField)(({ theme }) => ({
  "&.MuiTextField-root": {
    "& .Mui-focused:not(.Mui-error)": {
      color: theme.palette.common.white,
      "&.MuiInputBase-root::after": {
        borderBottomColor: theme.palette.common.white,
      },
    },
  },
}));
const RememberMeToggle = styled(FormControlLabel)(({ theme }) => ({
  color: theme.palette.common.white,
  "& .MuiCheckbox-root": {
    color: theme.palette.common.white,
  },
}));
const LoginButton = styled(LoadingButton)(() => ({
  backgroundColor: "#E19941",
  margin: "1.5em 0",
  "&.MuiButton-root": {
    color: "black",
    "&:hover": {
      backgroundColor: "#FFB949",
    },
    "&.Mui-disabled": {
      color: "rgba(255, 255, 255, 0.3)",
      boxShadow: "none",
      backgroundColor: "rgba(255, 255, 255, 0.12)",
    },
  },
}));
const ErrorItem = styled("li")(() => ({
  listStyleType: "none",
}));

type CredentialErrors = {
  general?: string[];
  email?: string;
  password?: string[];
};
